import type { BlockField, HeaderSection } from '@/blocks/domain/types';
import { sectionColumns } from '@/blocks/domain/helpers/headerSections';
import type { DocumentContext } from '../domain/DocumentContext';

export interface ExpandedField {
  /** Unique across the expanded list (a repeated field gets a per-site suffix). */
  key: string;
  field: BlockField;
  /** The context the field's tokens resolve against (`jobsite` is the site being printed). */
  context: DocumentContext;
  /** Text printed before the resolved value ("1. ", "2. "); empty for an ordinary field. */
  prefix: string;
}

const JOBSITE_TOKEN_RE = /\{\{\s*jobsite\./i;

export const referencesJobSite = (field: BlockField): boolean => JOBSITE_TOKEN_RE.test(field.value ?? '');

const readJobSites = (context: DocumentContext): Array<Record<string, unknown>> => {
  const sites = (context as { jobsites?: unknown }).jobsites;
  if (!Array.isArray(sites)) return [];
  return sites.filter((site): site is Record<string, unknown> => typeof site === 'object' && site !== null);
};

/**
 * Expands a field list for the document surfaces (HTML export + PDF). With two or more job sites,
 * the fields that reference `{{jobsite.*}}` print once per site, in site order, the first of each
 * group prefixed "1. ", "2. ", ... With zero or one site nothing changes: the host already puts
 * the first site on `context.jobsite`. Pure — shared by both renderers so they cannot diverge.
 */
export function expandJobSiteFields(fields: readonly BlockField[], context: DocumentContext): ExpandedField[] {
  const plain = (field: BlockField): ExpandedField => ({ key: field.id, field, context, prefix: '' });
  const sites = readJobSites(context);
  const siteFields = fields.filter(referencesJobSite);
  if (sites.length < 2 || siteFields.length === 0) return fields.map(plain);

  const result: ExpandedField[] = [];
  let expanded = false;
  for (const field of fields) {
    if (!referencesJobSite(field)) {
      result.push(plain(field));
      continue;
    }
    if (expanded) continue;
    expanded = true;
    sites.forEach((site, siteIndex) => {
      const siteContext = { ...context, jobsite: site } as DocumentContext;
      siteFields.forEach((siteField, fieldIndex) => {
        result.push({
          key: siteIndex === 0 ? siteField.id : `${siteField.id}:site-${siteIndex + 1}`,
          field: siteIndex === 0 ? siteField : { ...siteField, id: `${siteField.id}:site-${siteIndex + 1}` },
          context: siteContext,
          prefix: fieldIndex === 0 ? `${siteIndex + 1}. ` : ''
        });
      });
    });
  }
  return result;
}

/**
 * The same expansion for a block that stores real sections (LLAMS-844: the Bill to / Job site block).
 * Each column expands on its own, so a Job site column prints one numbered group per site and the Bill to
 * column next to it is untouched; the result stays in section / column / row order and every field keeps its
 * `sectionId` and `column`, so the section renderers lay it out unchanged. Hidden fields are dropped first.
 */
export function expandSectionedJobSiteFields(
  sections: readonly HeaderSection[],
  fields: readonly BlockField[],
  context: DocumentContext
): ExpandedField[] {
  const visible = fields.filter((field) => field.visible !== false);
  return sections.flatMap((section) => sectionColumns(section, visible).flatMap((column) => expandJobSiteFields(column, context)));
}
